import type { UserStatus } from '@repo-level/core-front';
import { AuthContext } from '@repo-level/core-front';
import { BonusStage } from 'api/common';
import chest1Img from 'assets/images/mission/chest-1.webp';
import chest2Img from 'assets/images/mission/chest-2.webp';
import chest3Img from 'assets/images/mission/chest-3.webp';
import chest4Img from 'assets/images/mission/chest-4.webp';
import chest5Img from 'assets/images/mission/chest-5.webp';
import chestLockedImg from 'assets/images/mission/chest-locked.webp';
import keyImg from 'assets/images/mission/key.webp';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EMissionBonusGroupKey } from '../helpers';

export const PRIZE_DONE_STATUSES = [
  BonusStage.Expired,
  BonusStage.Canceled,
  BonusStage.Activated,
  BonusStage.Finished,
  BonusStage.HandleBets,
] as string[];

export enum EMissionStatus {
  LOCKED = 'LOCKED',
  ACTIVE = 'ACTIVE',
  CLAIMED = 'CLAIMED',
}

const MISSION_CONFIG = [
  { character: 0, statusId: 'ENTRY_MISSION_COMPLETED', progress: 100 },
  { character: 1, statusId: 'L1_0%', progress: 0 },
  { character: 1, statusId: 'L1_25%', progress: 25 },
  { character: 1, statusId: 'L1_50%', progress: 50 },
  { character: 1, statusId: 'L1_75%', progress: 75 },
  { character: 1, statusId: 'L1_100%', progress: 100 },
  { character: 2, statusId: 'L2_0%', progress: 0 },
  { character: 2, statusId: 'L2_25%', progress: 25 },
  { character: 2, statusId: 'L2_50%', progress: 50 },
  { character: 2, statusId: 'L2_75%', progress: 75 },
  { character: 2, statusId: 'L2_100%', progress: 100 },
  { character: 3, statusId: 'L3_0%', progress: 0 },
  { character: 3, statusId: 'L3_25%', progress: 25 },
  { character: 3, statusId: 'L3_50%', progress: 50 },
  { character: 3, statusId: 'L3_75%', progress: 75 },
  { character: 3, statusId: 'L3_100%', progress: 100 },
  { character: 4, statusId: 'L4_0%', progress: 0 },
  { character: 4, statusId: 'L4_25%', progress: 25 },
  { character: 4, statusId: 'L4_50%', progress: 50 },
  { character: 4, statusId: 'L4_75%', progress: 75 },
  { character: 4, statusId: 'L4_100%', progress: 100 },
  { character: 5, statusId: 'L5_0%', progress: 0 },
  { character: 5, statusId: 'L5_25%', progress: 25 },
  { character: 5, statusId: 'L5_50%', progress: 50 },
  { character: 5, statusId: 'L5_75%', progress: 75 },
  { character: 5, statusId: 'L5_100%', progress: 100 },
];

const findCurrentStage = (userStatuses: UserStatus[]) => {
  const statusIds = new Set(userStatuses.map((s) => s.id));

  for (let i = MISSION_CONFIG.length - 1; i >= 0; i--) {
    if (statusIds.has(MISSION_CONFIG[i].statusId)) {
      return i;
    }
  }

  return -1;
};

const getMissionState = (
  character: number,
  currentStageIndex: number,
  groupKey: EMissionBonusGroupKey,
  prizes: Array<{ groupKey?: string; stage: string }>
) => {
  const isPrizeActivated = prizes.some(
    (prize) =>
      prize.groupKey === groupKey && PRIZE_DONE_STATUSES.includes(prize.stage)
  );

  if (currentStageIndex === -1) {
    return {
      status: character === 0 ? EMissionStatus.ACTIVE : EMissionStatus.LOCKED,
      progress: 0,
    };
  }

  const currentStage = MISSION_CONFIG[currentStageIndex];

  if (character < currentStage.character) {
    return {
      status: isPrizeActivated ? EMissionStatus.CLAIMED : EMissionStatus.ACTIVE,
      progress: 100,
    };
  }

  if (character === currentStage.character) {
    return {
      status: isPrizeActivated ? EMissionStatus.CLAIMED : EMissionStatus.ACTIVE,
      progress: currentStage.progress,
    };
  }

  if (
    character === currentStage.character + 1 &&
    currentStage.progress === 100
  ) {
    return { status: EMissionStatus.ACTIVE, progress: 0 };
  }

  return { status: EMissionStatus.LOCKED, progress: 0 };
};

export const useMissions = () => {
  const { t } = useTranslation(['common', 'mission']);
  const { user } = useContext(AuthContext);
  const { freespins, bonuses, lootboxes } = useContext(RealTimeServiceContext);

  const prizes = useMemo(
    () => [...freespins, ...bonuses, ...lootboxes],
    [freespins, bonuses, lootboxes]
  );

  const missions = useMemo(() => {
    const missionList = [
      {
        character: 0,
        prize: t('common:fs', { amount: 10 }),
        level: t('common:lvl', { x: 0 }),
        imgSrc: keyImg,
        descriptionKey: 'missions.0.description',
        groupKey: EMissionBonusGroupKey.L0,
      },
      {
        character: 1,
        prize: t('common:fs', { amount: 15 }),
        level: t('common:lvl', { x: 1 }),
        imgSrc: chest1Img,
        descriptionKey: 'missions.1.description',
        groupKey: EMissionBonusGroupKey.L1,
      },
      {
        character: 2,
        prize: t('common:fs', { amount: 30 }),
        level: t('common:lvl', { x: 2 }),
        imgSrc: chest2Img,
        descriptionKey: 'missions.2.description',
        groupKey: EMissionBonusGroupKey.L2,
      },
      {
        character: 3,
        prize: t('mission:wheel-spin'),
        level: t('common:lvl', { x: 3 }),
        imgSrc: chest3Img,
        descriptionKey: 'missions.3.description',
        groupKey: EMissionBonusGroupKey.L3,
      },
      {
        character: 4,
        prize: `$150 + ${t('common:fs', { amount: 100 })}`,
        level: t('common:lvl', { x: 4 }),
        imgSrc: chest4Img,
        descriptionKey: 'missions.4.description',
        groupKey: EMissionBonusGroupKey.L4,
      },
      {
        character: 5,
        prize: t('mission:wheel-spin'),
        level: t('common:lvl', { x: 5 }),
        imgSrc: chest5Img,
        descriptionKey: 'missions.5.description',
        groupKey: EMissionBonusGroupKey.L5,
      },
    ];

    if (!user) {
      return missionList.map((mission) => ({
        ...mission,
        status: EMissionStatus.LOCKED,
        progress: { current: 0, target: 100 },
      }));
    }

    const currentStageIndex = findCurrentStage(user.statuses || []);

    let lock = false;
    return missionList.map((mission) => {
      const { status, progress } = getMissionState(
        mission.character,
        currentStageIndex,
        mission.groupKey,
        prizes
      );
      if (lock)
        return {
          ...mission,
          status: EMissionStatus.LOCKED,
          progress: { current: 0, target: 100 },
        };
      if (status !== EMissionStatus.CLAIMED) lock = true;
      return {
        ...mission,
        status,
        progress: { current: progress, target: 100 },
      };
    });
  }, [user, prizes, t]);

  const currentMission = useMemo(() => {
    if (!user) {
      return {
        status: EMissionStatus.LOCKED,
        progress: {
          current: 0,
          target: 100,
        },
        character: 0,
        freespins: 'string',
        level: 'string',
        imgSrc: chestLockedImg,
        descriptionKey: 'non-auth-description-bottom',
      };
    }
    return (
      missions.find(({ status }) => status === EMissionStatus.ACTIVE) ||
      missions[missions.length - 1]
    );
  }, [user, missions]);

  return { missions, currentMission };
};
